import React from 'react';
import { GlassCard } from '@/design-system';

interface QuarterlyReadiness {
  quarter: number;
  readyPercentage: number;
  inRefinementPercentage: number;
  notReadyPercentage: number;
  totalFeatures: number;
}

interface QuarterlyReadinessChartProps {
  readiness: QuarterlyReadiness[] | null;
  isLoading: boolean;
}

const QuarterlyReadinessChart: React.FC<QuarterlyReadinessChartProps> = ({
  readiness,
  isLoading,
}) => {

  if (isLoading) {
    return (
      <GlassCard className="p-6 rounded-2xl">
        <div className="animate-pulse">
          <div className="h-6 bg-muted/50 dark:bg-muted/30 rounded w-1/2 mb-4"></div>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-muted/50 dark:bg-muted/30 rounded w-1/4"></div>
                <div className="h-8 bg-muted/50 dark:bg-muted/30 rounded w-full"></div>
                <div className="h-3 bg-muted/50 dark:bg-muted/30 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        </div>
      </GlassCard>
    );
  }

  if (!readiness || readiness.length === 0) {
    return (
      <GlassCard className="p-6 rounded-2xl">
        <h3 className="text-lg font-semibold text-foreground mb-4">Quarterly Feature Readiness</h3>
        <div className="text-center text-muted-foreground py-8">
          No readiness data available
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Quarterly Feature Readiness</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Features meeting 80+ readiness score</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 rounded-lg text-sm font-medium bg-accent-blue-bg text-accent-blue">Features</button>
          <button className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-surface-muted">TWs</button>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="flex items-end gap-4 h-48">
        {readiness.map((quarter) => (
          <div key={quarter.quarter} className="flex-1 flex flex-col items-center gap-2">
            <div className="w-full flex items-end gap-1 h-40">
              <div
                className="flex-1 bg-gradient-to-t from-status-success to-status-success-hover rounded-t-lg transition-all hover:brightness-110"
                style={{ height: `${quarter.readyPercentage}%` }}
              ></div>
              <div
                className="flex-1 bg-gradient-to-t from-status-warning to-status-warning-hover rounded-t-lg transition-all hover:brightness-110"
                style={{ height: `${quarter.inRefinementPercentage}%` }}
              ></div>
              <div
                className="flex-1 bg-gradient-to-t from-muted to-muted-foreground rounded-t-lg transition-all hover:brightness-110"
                style={{ height: `${quarter.notReadyPercentage}%` }}
              ></div>
            </div>
            <span className="text-sm font-medium text-muted-foreground">Q{quarter.quarter}</span>
            <span className="text-xs text-muted-foreground">{quarter.totalFeatures} features</span>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2 text-sm">
          <div className="w-3 h-3 rounded bg-status-success"></div>
          <span className="text-muted-foreground">Shovel Ready (80+)</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="w-3 h-3 rounded bg-status-warning"></div>
          <span className="text-muted-foreground">In Refinement (40-79)</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="w-3 h-3 rounded bg-muted"></div>
          <span className="text-muted-foreground">Not Ready (0-39)</span>
        </div>
      </div>
    </GlassCard>
  );
};

export default QuarterlyReadinessChart;